import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import EditableField from "@/components/EditableField";
import { PORTAL_NAV_MODULES, portalHost, hslTripleToHex, toHslTriple, buildPreviewTheme } from "@/lib/tenant-theme";

const LANGUAGES = [
  { id: "en-US", label: "English (US)" },
  { id: "en-GB", label: "English (UK)" },
  { id: "es-ES", label: "Spanish" },
  { id: "fr-FR", label: "French" },
];
const TIMEZONES = [
  { id: "America/New_York", label: "(UTC-05:00) Eastern Time (US & Canada)" },
  { id: "America/Chicago", label: "(UTC-06:00) Central Time (US & Canada)" },
  { id: "America/Los_Angeles", label: "(UTC-08:00) Pacific Time (US & Canada)" },
  { id: "Europe/London", label: "(UTC+00:00) London" },
  { id: "Asia/Kolkata", label: "(UTC+05:30) India Standard Time" },
];

const PORTAL_LABELS = { talent: "Talent Portal", hub: "Hub Portal", serviqo: "ServiQO" };
const PORTAL_SUBTITLE = {
  talent: "For candidates, talent and external users.",
  hub: "For internal teams and administrators.",
  serviqo: "Email-to-case service desk for support teams.",
};

const PORTAL_COLOR_FIELDS = [
  { key: "primary", label: "Primary" },
  { key: "secondary", label: "Secondary" },
  { key: "accent", label: "Accent" },
  { key: "background", label: "Background" },
];

function PortalCard({ portal, config, onChange, brandColors }) {
  // Defense in depth: a portal selected at signup may not have a seeded entry
  // in featureConfig.portals (e.g. a newly-added portal like serviqo). Fall
  // back to an empty shape so reading config.colors/nav can never throw and
  // white-screen the whole wizard.
  const safeConfig = config || { displayName: "", tagline: "", subdomain: "", nav: {} };
  const set = (patch) => onChange({ ...safeConfig, ...patch });
  const setNav = (id, val) => onChange({ ...safeConfig, nav: { ...safeConfig.nav, [id]: val } });
  const modules = PORTAL_NAV_MODULES[portal] || [];

  const portalColors = safeConfig.colors || {};
  const setColor = (key, hex) => {
    const triple = toHslTriple(hex);
    onChange({ ...safeConfig, colors: { ...portalColors, [key]: triple || "" } });
  };
  const clearColor = (key) => {
    const next = { ...portalColors };
    delete next[key];
    onChange({ ...config, colors: next });
  };
  // Effective color for the picker: this portal's override, else the
  // shared brand default — so an un-overridden swatch shows the inherited
  // brand color, not black.
  const effective = (key) => portalColors[key] || brandColors?.[key];

  return (
    <Card data-testid={`portal-config-${portal}`}>
      <CardHeader>
        <CardTitle className="text-base">{PORTAL_LABELS[portal]}</CardTitle>
        <CardDescription>{PORTAL_SUBTITLE[portal]}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <EditableField
          label="Portal name"
          required
          value={safeConfig.displayName || ""}
          onChange={(v) => set({ displayName: v })}
          placeholder={`${PORTAL_LABELS[portal]} name`}
          emptyText="Click to add a portal name"
          testId={`${portal}-display-name`}
        />
        <EditableField
          label="Tagline"
          value={safeConfig.tagline || ""}
          onChange={(v) => set({ tagline: v })}
          placeholder="A short tagline shown on the portal"
          emptyText="No tagline yet — click to add one"
          multiline
          testId={`${portal}-tagline`}
        />
        {/* Per-portal logo override. Stored at featureFlags.portals.<portal>
            .logoUrl and merged server-side over the shared brand logo
            (base fallback) — so a tenant can give Talent and Hub distinct
            logos while an un-set portal inherits the brand logo. Plain URL,
            same UX as the other per-portal text fields. */}
        <EditableField
          label="Logo URL"
          value={safeConfig.logoUrl || ""}
          onChange={(v) => set({ logoUrl: v })}
          placeholder="https://cdn.example.com/logo.svg"
          emptyText="No portal logo — inherits your brand logo. Click to add one."
          testId={`${portal}-logo-url`}
        />
        <div>
          <Label>Portal URL</Label>
          <div className="mt-1.5 rounded-md border border-input bg-muted px-3 py-2 text-sm text-muted-foreground" data-testid={`${portal}-portal-url`}>
            {portalHost(portal)}
          </div>
          <div className="mt-2">
            <Label htmlFor={`${portal}-subdomain`} className="text-xs text-muted-foreground">Custom domain prefix (optional, coming soon)</Label>
            <div className="mt-1 flex items-center">
              <Input id={`${portal}-subdomain`} data-testid={`${portal}-subdomain`} className="rounded-r-none" placeholder="acme"
                value={safeConfig.subdomain || ""} onChange={(e) => set({ subdomain: e.target.value.replace(/[^a-z0-9-]/gi, "").toLowerCase() })} />
              <span className="rounded-r-md border border-l-0 border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                .{portal}.transcendit.io
              </span>
            </div>
          </div>
        </div>

        {/* Per-portal color overrides. Empty = inherit the shared brand, so
            Talent and Hub look identical by default and a tenant can
            diverge them where they choose. */}
        <div>
          <Label>Portal colors</Label>
          <p className="mt-0.5 text-xs text-muted-foreground">Defaults to your brand colors. Override to make this portal look different.</p>
          <div className="mt-2 grid grid-cols-2 gap-2" data-testid={`${portal}-colors`}>
            {PORTAL_COLOR_FIELDS.map(({ key, label }) => {
              const overridden = Boolean(portalColors[key]);
              return (
                <div key={key} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
                  <input
                    type="color"
                    aria-label={`${PORTAL_LABELS[portal]} ${label} color`}
                    data-testid={`${portal}-color-${key}`}
                    value={hslTripleToHex(effective(key))}
                    onChange={(e) => setColor(key, e.target.value)}
                    className="h-6 w-6 shrink-0 cursor-pointer rounded border border-input bg-transparent p-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">{label}</p>
                    <p className="text-[10px] text-muted-foreground">{overridden ? "Custom" : "Inherited"}</p>
                  </div>
                  {overridden && (
                    <button type="button" onClick={() => clearColor(key)} data-testid={`${portal}-color-${key}-reset`}
                      className="text-[10px] font-medium text-primary hover:underline">Reset</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Navigation modules</Label>
          <div className="mt-2 space-y-2" data-testid={`${portal}-nav-modules`}>
            {modules.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <span className="text-sm">{m.label}</span>
                <Switch
                  checked={config.nav?.[m.id] !== false}
                  onCheckedChange={(v) => setNav(m.id, v)}
                  data-testid={`${portal}-nav-${m.id}`}
                  aria-label={`${PORTAL_LABELS[portal]} ${m.label}`}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Live navigation synced FROM the portal app itself (one-way:
            portal -> tenant backend). Read-only — the portal owns its own
            menu; this just mirrors what it actually renders so this page
            reflects reality instead of the abstract toggles above. Shown
            only once the portal has published its registry. */}
        {config.navRegistry?.items?.length > 0 && (
          <div data-testid={`${portal}-nav-registry`}>
            <div className="flex items-center justify-between">
              <Label>Live navigation</Label>
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                synced from {config.navRegistry.portal || portal} · {config.navRegistry.items.length} items
              </span>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The portal's actual left-navigation, published by the {PORTAL_LABELS[portal]}. Read-only here —
              it stays in sync with what the portal shows.
            </p>
            <ul className="mt-2 divide-y divide-border rounded-md border border-border" data-testid={`${portal}-nav-registry-list`}>
              {config.navRegistry.items.map((item) => (
                <li key={item.key} className="flex items-center justify-between px-3 py-1.5">
                  <span className="text-sm">{item.label}</span>
                  <span className="flex items-center gap-2">
                    <code className="text-[10px] text-muted-foreground">{item.path}</code>
                    {item.roles?.length > 0 && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium text-amber-700">
                        {item.roles.join(", ")}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            {config.navRegistry.syncedAt && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                Last synced {new Date(config.navRegistry.syncedAt).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PortalConfigFields({ featureConfig, onChange, portals = ["talent", "hub"], branding = null }) {
  const setPortal = (portal, cfg) => onChange({ ...featureConfig, portals: { ...featureConfig.portals, [portal]: cfg } });
  // ServiQO is entitled/selectable like any portal, but it is NOT brandable —
  // it has no per-portal look-and-feel of its own, so it never gets a branding
  // card here (the user: "no branding required for serviqo"). Other portals
  // render normally.
  const brandablePortals = portals.filter((p) => p !== "serviqo");
  const setLocale = (patch) => onChange({ ...featureConfig, locale: { ...featureConfig.locale, ...patch } });
  const brandColors = buildPreviewTheme(branding).colors;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        {brandablePortals.map((p) => (
          <PortalCard key={p} portal={p} config={featureConfig.portals[p]} onChange={(cfg) => setPortal(p, cfg)} brandColors={brandColors} />
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="default-language">Default language</Label>
          <Select value={featureConfig.locale?.defaultLanguage} onValueChange={(v) => setLocale({ defaultLanguage: v })}>
            <SelectTrigger id="default-language" className="mt-1.5" data-testid="default-language"><SelectValue /></SelectTrigger>
            <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={featureConfig.locale?.timezone} onValueChange={(v) => setLocale({ timezone: v })}>
            <SelectTrigger id="timezone" className="mt-1.5" data-testid="timezone"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
